// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';

import { usePageTitle } from '@/components/app-shell/AppShell';
import { PageHeader } from '@/components/greenlight/PageHeader';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { AgentSetup } from '@/components/settings/AgentSetup';
import { api } from '@/lib/api';
import { useCurrentUser } from '@/lib/auth';

export function AgentSetupPage(): React.JSX.Element {
  usePageTitle('Agent setup');
  const user = useCurrentUser();

  const { data, isLoading, isError } = api.settings.get.useQuery(['settings'], undefined, {
    queryKey: ['settings'],
  });
  const settings = data?.status === 200 ? data.body : undefined;

  return (
    <div className="space-y-6" data-testid="agent-setup-page">
      {isError ? (
        <>
          <PageHeader title="Agent setup" />
          <Alert variant="destructive" data-testid="agent-setup-error">
            <AlertTitle>Couldn&apos;t load settings.</AlertTitle>
            <AlertDescription>
              Something went wrong fetching the agent setup configuration. Retry in a moment.
            </AlertDescription>
          </Alert>
        </>
      ) : isLoading ? (
        <>
          <PageHeader title="Agent setup" />
          <Skeleton className="h-40 rounded-gl-lg" />
          <Skeleton className="h-40 rounded-gl-lg" />
        </>
      ) : !settings ? (
        // A resolved non-2xx (4xx/5xx body) leaves isError false but settings
        // undefined — still an error to the user, never a blank page.
        <>
          <PageHeader title="Agent setup" />
          <Alert variant="destructive" data-testid="agent-setup-error">
            <AlertTitle>Couldn&apos;t load settings.</AlertTitle>
            <AlertDescription>
              Something went wrong fetching the agent setup configuration. Retry in a moment.
            </AlertDescription>
          </Alert>
        </>
      ) : (
        <>
          <PageHeader eyebrow={settings.org?.display_name ?? 'Agent setup'} title="Agent setup" />
          <AgentSetup agentSetup={settings.agent_setup} userRole={user?.role} />
        </>
      )}
    </div>
  );
}
